import { Button, Group, Text } from '@repletoryreach/sdk-ui';
import { DataTable } from '@repletoryreach/sdk-ui/data-display';
import type { admin } from '@repletoryreach/sdk-api';
import type { Indexed } from '../../../../lib/types';
import { formatInteger } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';
import { LanguageNameCell, LanguageSwitch, LinesCell, MetaCodesCell, QualityBadge, SpeechCell } from './cells';

export interface LanguageRowsProps {
  languages: admin.AdminLanguage[];
  busy: string | null;
  onToggle: (l: admin.AdminLanguage, on: boolean) => void;
  onEdit: (l: admin.AdminLanguage) => void;
  onLines: (l: admin.AdminLanguage) => void;
}

/**
 * One region's languages as a table. Every region's table carries the same columns at the same widths, so the
 * groups line up down the page and read as one list.
 */
export function LanguagesTable({ languages, busy, onToggle, onEdit, onLines }: LanguageRowsProps) {
  const t = useT();
  return (
    <DataTable<Indexed<admin.AdminLanguage>>
      records={languages as Indexed<admin.AdminLanguage>[]}
      idAccessor="code"
      emptyState={t('billing.languages.empty')}
      columns={[
        { accessor: 'native_name', title: t('billing.languages.columns.language'), width: '18%', render: (l) => <LanguageNameCell language={l} /> },
        { accessor: 'quality', title: t('billing.languages.columns.quality'), width: '10%', render: (l) => <QualityBadge quality={l.quality} /> },
        { accessor: 'meta_codes', title: t('billing.languages.columns.meta'), width: '14%', render: (l) => <MetaCodesCell language={l} /> },
        { accessor: 'speech_code', title: t('billing.languages.columns.speech'), width: '9%', render: (l) => <SpeechCell language={l} /> },
        {
          accessor: 'bots_offering',
          title: t('billing.languages.columns.bots'),
          width: '9%',
          render: (l) => <Text role="bodySmall">{formatInteger(l.bots_offering)}</Text>
        },
        { accessor: 'wordings', title: t('billing.languages.columns.lines'), width: '17%', render: (l) => <LinesCell language={l} /> },
        { accessor: 'enabled', title: t('billing.languages.columns.offered'), width: '9%', render: (l) => <LanguageSwitch language={l} busy={busy === l.code} onToggle={onToggle} /> },
        {
          accessor: 'actions',
          title: t('common.actions'),
          textAlign: 'right',
          render: (l) => (
            <Group gap="xs" wrap="nowrap" justify="flex-end">
              <Button size="xs" variant="outline" onClick={() => onLines(l)} aria-label={t('billing.languages.lines.actionFor', { name: l.name })}>
                {t('billing.languages.lines.action')}
              </Button>
              <Button size="xs" variant="subtle" onClick={() => onEdit(l)} aria-label={t('billing.languages.edit.actionFor', { name: l.name })}>
                {t('action.edit')}
              </Button>
            </Group>
          )
        }
      ]}
    />
  );
}
